import { useNavigate } from 'react-router-dom';
import { useStatsigClient } from '@statsig/react-bindings';

import ListPage from '../../components/ListPage';

import Copy from '../../components/ListPage/components/ActionsToolbar/components/Copy';
import Delete from '../../components/ListPage/components/ActionsToolbar/components/Delete';
import ForceComplete from '../../components/ListPage/components/ActionsToolbar/components/ForceComplete';
import Rename from '../../components/ListPage/components/ActionsToolbar/components/Rename';
import Share from '../../components/ListPage/components/ActionsToolbar/components/Share';

import {
  deleteInstantSurvey,
  forceCompleteInstantSurvey,
  patchInstantSurvey,
} from '@api/instant_survey';
import {
  deleteSurveys,
  readSurveys,
  surveyPageSize,
  surveyStatus,
  updateSurveyTitle,
  shareInstantSurveyWithClientGroup,
  shareInstantSurveyWithUsers,
} from '@api/surveys';

import { getFilterOptions, getItemColumns } from './utilities/helpers.js';
import { GATES } from '@constants';

const Surveys = () => {
  const navigate = useNavigate();
  const { client } = useStatsigClient();
  const useCalypsoStatus = client.checkGate(GATES.USE_CALYPSO_AS_SURVEY_PROVIDER);

  const expandSurveyRow = ({ atlasInstantSurveyRef, id, instantSurveyTypeId, status }) => {
    switch (status) {
      case surveyStatus.DRAFT:
        const params = { id };
        if (atlasInstantSurveyRef) params.ref = atlasInstantSurveyRef;
        navigate({
          pathname: `/dashboard/create-survey/${instantSurveyTypeId}`,
          search: new URLSearchParams(params).toString(),
        });
        break;
      case surveyStatus.COMPLETED:
      case surveyStatus.MET_SAMPLE:
        navigate({ pathname: `/dashboard/survey/${id}` });
        break;
      default:
        break;
    }
  };

  const handleDeleteSurveys = async (surveys) => {
    return await Promise.all([
      deleteSurveys({ ids: surveys.map(({ id }) => id) }),
      ...surveys
        .filter(({ atlasInstantSurveyRef }) => atlasInstantSurveyRef)
        .map(({ atlasInstantSurveyRef }) => deleteInstantSurvey({ id: atlasInstantSurveyRef })),
    ]);
  };

  const handleRenameSurvey = async ({ name, row: survey }) => {
    return await Promise.all([
      updateSurveyTitle({ id: survey.id, name }),
      ...(survey.atlasInstantSurveyRef
        ? [patchInstantSurvey({ data: { title: name }, id: survey.atlasInstantSurveyRef })]
        : []),
    ]);
  };

  const handleForceCompleteSurvey = async (survey) => {
    return await forceCompleteInstantSurvey({ atlasSurveyRef: survey.atlasInstantSurveyRef });
  };

  return (
    <ListPage
      filterOptions={getFilterOptions(useCalypsoStatus)}
      itemActions={[
        <ForceComplete
          handleForceCompleteSurvey={handleForceCompleteSurvey}
          key="force-complete-item"
        />,
        <Copy key="copy-item" redirect="/dashboard/create-survey" type="Survey" />,
        <Rename key="rename-item" type="Survey" updateName={handleRenameSurvey} />,
        <Share
          hasAllColleagues
          hasFindColleagues
          hasSharePublicly
          hideOnMultiSelect
          isSurvey={true}
          key="share-item"
          shareWithClient={shareInstantSurveyWithClientGroup}
          shareWithUsers={shareInstantSurveyWithUsers}
          type="survey"
        />,
        <Delete deleteItems={handleDeleteSurveys} key="delete-item" />,
      ]}
      itemColumns={getItemColumns(useCalypsoStatus)}
      itemSize={surveyPageSize}
      onCreate={() => navigate('/dashboard/verifiedvoices/instant-surveys')}
      onExpandItemRow={expandSurveyRow}
      readItems={readSurveys}
      type="Survey"
    />
  );
};

export default Surveys;
